import { useCallback, useRef, useState } from 'react';

/**
 * Alert 상태 관리 커스텀 훅
 * @returns {Object} Alert 상태 및 제어 함수
 */
function useAlert() {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
    disableOverlayClick: false,
  });

  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  // 알럿 확인 버튼 클릭 시 실행할 콜백 (ref 사용으로 의존성 변경 없이 안정적 참조, 무한 루프 방지)
  const onCloseCallbackRef = useRef(null);
  // onConfirm이 호출되었는지 추적하는 ref (Alert에서 onConfirm 후 onClose도 호출되므로 필요)
  const onConfirmCalledRef = useRef(false);
  // onCancel 콜백을 저장하는 ref (클로저 문제 방지)
  const onCancelRef = useRef(null);

  /**
   * 알럿 확인 버튼 클릭 시: ref에 저장된 콜백 실행 후 알럿 닫기.
   * - ref + 안정된 핸들러만 Alert에 전달해 useEffect 의존성 변경으로 인한 무한 루프를 막음.
   */
  const handleAlertConfirm = useCallback(function () {
    const fn = onCloseCallbackRef.current;
    onCloseCallbackRef.current = null;
    setAlertState({ isOpen: false, message: '', disableOverlayClick: false });
    if (typeof fn === 'function') {
      fn();
    }
  }, []);

  /**
   * 알럿을 띄우고, 확인 버튼 클릭 시 실행할 콜백을 등록합니다.
   *
   * @param {string} message - 알럿 메시지
   * @param {Function} [onCloseCallback] - 확인 버튼 클릭 시 실행할 콜백 (이동 등)
   */
  function showAlert(message, onCloseCallback) {
    onCloseCallbackRef.current = typeof onCloseCallback === 'function' ? onCloseCallback : null;
    setAlertState({
      isOpen: true,
      message,
      // 확인 콜백이 있는 경우(상신 완료 등) 오버레이 클릭으로 닫기 방지 → 확인 버튼으로만 이동 처리
      disableOverlayClick: typeof onCloseCallback === 'function',
    });
  }

  function closeAlert() {
    onCloseCallbackRef.current = null;
    setAlertState({
      isOpen: false,
      message: '',
      disableOverlayClick: false,
    });
  }

  /**
   * 컨펌 알럿을 띄웁니다.
   *
   * @param {string} message - 컨펌 메시지
   * @param {Function} onConfirm - '예'를 눌렀을 때 실행할 콜백
   * @param {Function} [onCancel] - '아니요'를 눌렀을 때 실행할 콜백 (선택사항)
   */
  function showConfirm(message, onConfirm, onCancel) {
    // ref 초기화
    onConfirmCalledRef.current = false;
    onCancelRef.current = typeof onCancel === 'function' ? onCancel : null;

    setConfirmState({
      isOpen: true,
      message,
      onConfirm: function () {
        // onConfirm 호출 플래그 설정 (closeConfirm에서 onCancel 실행 방지)
        onConfirmCalledRef.current = true;
        setConfirmState((prev) => ({ ...prev, isOpen: false }));
        if (typeof onConfirm === 'function') onConfirm();
      },
      onCancel: null,
    });
  }

  function closeConfirm() {
    // onConfirm이 이미 호출된 경우 onCancel 실행하지 않음
    // (Alert 컴포넌트에서 onConfirm 후 onClose도 호출되기 때문)
    if (onConfirmCalledRef.current) {
      setConfirmState({
        isOpen: false,
        message: '',
        onConfirm: null,
        onCancel: null,
      });
      return;
    }

    // 취소 콜백 실행 (ref 사용으로 클로저 문제 방지)
    const cancelCallback = onCancelRef.current;
    onCancelRef.current = null;

    setConfirmState({
      isOpen: false,
      message: '',
      onConfirm: null,
      onCancel: null,
    });

    if (cancelCallback) {
      cancelCallback();
    }
  }

  return {
    alertState,
    showAlert,
    closeAlert,
    handleAlertConfirm,
    confirmState,
    showConfirm,
    closeConfirm,
  };
}

export default useAlert;
